/**
 * Resolve the display name of a customer for UI rendering, contracts,
 * email subjects, and PDF headers.
 *
 * Business customers do not have ``first_name`` / ``last_name`` filled
 * — the dealer populates ``company_name`` instead. The legacy code
 * unconditionally rendered ``first_name + ' ' + last_name`` which
 * produced an empty string for every Ragione Sociale, breaking 4
 * customer-facing surfaces at once:
 *   • ContractModal "Dati del Contraente" header
 *   • "Complimenti, NAME!" celebration screen
 *   • Admin "Nuovo Contratto Firmato" notification email
 *   • Internal customer-name labels in admin pages
 *
 * Bug reported by Fabrizio Guerra on 2026-06-09 (DLR-4YEWPE business
 * customer flow). Helper kept tiny and dependency-free so it can be
 * imported from both customer-facing and admin pages.
 *
 * @param {Object|null|undefined} customer
 * @returns {string} The display name. Falls back to an empty string
 *                   only when the customer object lacks any usable
 *                   field — the caller decides what to substitute.
 */
export const getCustomerDisplayName = (customer) => {
  if (!customer) return '';
  const isBusiness = (customer.customer_type || 'private') === 'business';
  if (isBusiness) {
    return (customer.company_name || '').trim();
  }
  const first = (customer.first_name || '').trim();
  const last = (customer.last_name || '').trim();
  return `${first} ${last}`.trim();
};

/**
 * Same as ``getCustomerDisplayName`` but returns a "best guess" first
 * name for greeting screens ("Complimenti, NAME!"). Business clients
 * see the company name; private clients see only their given name.
 */
export const getCustomerGreetingName = (customer) => {
  if (!customer) return '';
  const isBusiness = (customer.customer_type || 'private') === 'business';
  if (isBusiness) {
    return (customer.company_name || '').trim();
  }
  return (customer.first_name || '').trim();
};
